<template>
  <div v-if="s.active" class="fvp">
    <div class="fvp__stage" @click="togglePlay">
      <video
        ref="video"
        class="fvp__video"
        :src="s.src"
        :poster="s.poster || undefined"
        autoplay
        playsinline
        @loadedmetadata="onLoaded"
        @timeupdate="onTime"
        @play="paused = false"
        @pause="paused = true"
        @ended="dismiss"
      ></video>
      <span v-if="paused" class="fvp__play" aria-hidden="true">
        <v-icon size="30" color="#fff">mdi-play</v-icon>
      </span>
    </div>
    <div class="fvp__bar">
      <button type="button" class="fvp__title" :title="s.title" @click="expand">{{ s.title }}</button>
      <button type="button" class="fvp__btn" :disabled="!ready" aria-label="작은 화면 (잠금/백그라운드 재생)" @click="togglePip">
        <v-icon size="16">mdi-picture-in-picture-bottom-right</v-icon>
      </button>
      <button type="button" class="fvp__btn" aria-label="크게 보기" @click="expand">
        <v-icon size="16">mdi-arrow-expand</v-icon>
      </button>
      <button type="button" class="fvp__btn" aria-label="닫기" @click="dismiss">
        <v-icon size="16">mdi-close</v-icon>
      </button>
    </div>
  </div>
</template>

<script>
import { floatingPlayer } from "@/services/floatingPlayer";
import { auth } from "@/firebaseConfig";
import masterclassProgressService from "@/api/masterclassProgressService";
import { accumulate, FLUSH_EVERY_SEC } from "@/services/watchTime";

export default {
  name: "FloatingVideoPlayer",
  data() {
    return {
      paused: false,
      ready: false,
      // Playback accounting for the weekly goal — see services/watchTime.js.
      acc: { lastTime: 0, pending: 0, total: 0 },
    };
  },
  computed: {
    s() {
      return floatingPlayer.state;
    },
  },
  beforeUnmount() {
    // Don't drop the final partial batch when the player closes.
    this.flushWatch(this.acc.lastTime);
  },
  methods: {
    onLoaded() {
      const v = this.$refs.video;
      if (!v) return;
      this.ready = true;
      if (this.s.currentTime > 0) v.currentTime = this.s.currentTime;
      // Autoplay may be blocked on a fresh element / after a refresh — reflect the
      // real state so the play overlay shows and the user can tap to resume.
      v.play().then(() => { this.paused = false; }).catch(() => { this.paused = true; });
    },
    onTime() {
      const v = this.$refs.video;
      if (!v) return;
      floatingPlayer.setTime(v.currentTime);
      // Playback here counts toward the weekly goal exactly as it does in the
      // lesson view. Previously this handler only synced the time pointer, so
      // every minute watched through the mini-player was silently uncounted.
      this.acc = accumulate(this.acc, v.currentTime, FLUSH_EVERY_SEC, v.playbackRate);
      if (this.acc.shouldFlush) this.flushWatch(v.currentTime);
    },
    /** Batched write of accumulated playback (§11). Fail-soft, like every
     *  other progress write — a lost flush costs minutes, never the session. */
    flushWatch(positionSec) {
      // This component is mounted globally in App.vue with no userInfo prop,
      // and the shared state carries `lessonId` (not `videoId`), so both come
      // from their real sources rather than an assumed shape.
      const uid = auth.currentUser?.uid;
      const videoId = floatingPlayer.state?.lessonId;
      const secs = this.acc.pending;
      if (!uid || !videoId || secs <= 0) return;
      this.acc = { ...this.acc, pending: 0 };
      masterclassProgressService.savePosition(uid, videoId, positionSec || 0, secs);
    },
    togglePlay() {
      const v = this.$refs.video;
      if (!v) return;
      if (v.paused) v.play().catch(() => {});
      else v.pause();
    },
    // CONTROLLED Picture-in-Picture — replaces the flaky native button so the
    // request is always caught: a NotAllowedError (tapped before ready / denied)
    // can never escape as an uncaught rejection. This is the path to lock-screen /
    // background playback. Handles both the standard API and iOS Safari's webkit
    // presentation-mode API.
    async togglePip() {
      const v = this.$refs.video;
      if (!v || v.readyState < 1) return; // not ready yet — a PiP request would fail
      try {
        if (typeof v.webkitSetPresentationMode === "function") {
          const next = v.webkitPresentationMode === "picture-in-picture" ? "inline" : "picture-in-picture";
          v.webkitSetPresentationMode(next);
          return;
        }
        if (document.pictureInPictureElement === v) await document.exitPictureInPicture();
        else if (document.pictureInPictureEnabled) await v.requestPictureInPicture();
      } catch (e) {
        /* not ready / denied — silently ignore; the user can retry once playing */
      }
    },
    expand() {
      // Leave the player active until the lesson mounts + takes over (it reads
      // currentTime, then closes us) so playback never visibly gaps.
      if (this.s.lessonId) this.$router.push(`/lesson/${this.s.lessonId}`);
      else this.dismiss();
    },
    dismiss() {
      const v = this.$refs.video;
      if (v) {
        v.pause();
        v.removeAttribute("src");
        v.load();
      }
      floatingPlayer.close();
    },
  },
};
</script>

<style scoped>
.fvp {
  position: fixed;
  right: 12px;
  bottom: 12px;
  z-index: 2000;
  width: min(240px, 64vw);
  background: #161618;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}
.fvp__stage {
  position: relative;
  cursor: pointer;
}
.fvp__video {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
}
.fvp__play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.28);
  pointer-events: none;
}
.fvp__bar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
}
.fvp__title {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
  background: none;
  border: none;
  text-align: left;
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}
.fvp__btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s ease;
}
.fvp__btn:hover {
  background: rgba(255, 255, 255, 0.12);
}
.fvp__btn:disabled {
  opacity: 0.4;
  cursor: default;
}
</style>
